import dynamic from "next/dynamic";
import Link from "next/link";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "@styles/cssinjs/helpers/variables";
const TpWidgetMicro = dynamic(() =>
  import("@components/organisms/common/TpWidgetMicro")
);
const GetProFreePlanCards = dynamic(() =>
  import("@components/organisms/common/GetProFreePlanCards")
);

export default function GetProFreePlan({
  content,
  pricing,
  initialPlanType,
  className = "",
  pageName,
  isHero,
  locale,
  hasGeoOverrides = false,
  showCurrencyCode = true,
}) {
  const isAnnualBillingPlan = initialPlanType === "annual-billing-plan";
  const isMonthlyBillingPlan = initialPlanType === "monthly-billing-plan";
  return (
    <article
      className={`pricing-wrap has-align-top pricing-container get-pro-free-plan container lg:pt-5 pb-10 ${className}`}
      id={initialPlanType}
    >
      <div className="fx fx--sb  get-pro-free-plan__wrap">
        <div className="get-pro-free-plan__content fx fx--c">
          {isHero ? (
            <h1
              className="text-lg mt-3"
              dangerouslySetInnerHTML={{ __html: content.heading }}
            />
          ) : (
            <h2
              className="text-lg lg:mt-3 md:mt-5 md:mb-3 get-pro-heading"
              dangerouslySetInnerHTML={{ __html: content?.heading }}
            />
          )}
          <div
            className={`desk-only get-pro-review ${
              hasGeoOverrides && content.smsDisclaimer
                ? "has-geo-disclaimer"
                : ""
            }`}
          >
            <p
              className="lg:mb-2 lg:maxw-lg lg:ml-auto lg:mr-auto md:text-left"
              dangerouslySetInnerHTML={{ __html: content.desc }}
            />
            <p
              className=" lg:maxw-lg lg:ml-auto lg:mr-auto md:text-left"
              dangerouslySetInnerHTML={{ __html: content.reviewer }}
            />
            <TpWidgetMicro className="trustpilot-widget-micro lg:mt-2 md:mt-6 " />
          </div>
          <p
            className="text-xs mb-3 desk-only get-pro-notes"
            dangerouslySetInnerHTML={{ __html: content.note }}
          />
          {hasGeoOverrides && content.smsDisclaimer && (
            <p
              className="text-xs mb-5 get-pro-notes"
              dangerouslySetInnerHTML={{ __html: content.smsDisclaimer }}
            />
          )}
        </div>
        <div className="get-pro-free-plan__cards">
          <GetProFreePlanCards
            toggle={pricing.toggle}
            plans={pricing.plans}
            isAnnualBillingPlan={isAnnualBillingPlan}
            isMonthlyBillingPlan={isMonthlyBillingPlan}
            showCurrencyCode={showCurrencyCode}
          />
        </div>
      </div>
      {content.explore ? (
        <div className="mx-auto text-center mt-3 fx fx--sb notes">
          <Link
            href={content.explore.href}
            className="green-text text-center mx-auto md:mb-5 fx fx--cv fx--sb"
            id="pro-cta-plan-button"
          >
            Explore all features
            {isHero ? (
              <span className="sort-down">
                &nbsp;
                <svg width="25" height="24" className="setmore-emblem">
                  <use href={`/sprite.svg#sort-down`}></use>
                </svg>
              </span>
            ) : null}
          </Link>
          <div>
            <p
              className="text-xs mb-5 text-left get-pro-notes"
              dangerouslySetInnerHTML={{ __html: content.note }}
            />
            {hasGeoOverrides && content.smsDisclaimer && (
              <p
                className="text-xs text-left get-pro-notes"
                dangerouslySetInnerHTML={{ __html: content.smsDisclaimer }}
              />
            )}
          </div>
        </div>
      ) : null}
      <style jsx>{`
        @media (min-width: ${viewport.tablet
            .default}) and (max-width: ${viewport.tablet.md}) {
          .notes {
            gap: 38px;
            align-items: flex-start !important;
          }
          .notes a {
            width: 55%;
          }
          .notes div {
            width: 47%;
            margin-bottom: 0 !important;
          }
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .notes {
            gap: 0 !important;
          }
          .notes a,
          .notes p {
            width: 100%;
            text-align: left !important;
          }
        }
        @media (min-width: ${viewport.tablet.md}) {
          .notes p {
            display: none;
          }
        }
        .pricing-wrap .card--default > * {
          position: relative;
          padding: 20px 32px 104px;
          margin-bottom: 20px;
          font-size: ${font.size.sm[3]};
          line-height: ${lineHeight[6]};
        }
        ${pageName === "Pricing Root Page"
          ? ``
          : `
          .pricing-wrap .card--default > * {
              box-shadow: 0 1px 2px 0 rgba(48, 58, 71, 0.2), 0 1px 3px 0 rgba(48, 58, 71, 0.1);          
          }
          `}
        .pricing-wrap .card--default > :hover {
          box-shadow: 0 14px 32px 0 rgba(22, 45, 61, 0.08),
            0 1px 4px 0 rgba(22, 45, 61, 0.1);
        }
        .pricing-wrap .card--default .plan-cta {
          min-height: 88px;
          position: absolute;
          width: calc(100% - 78px);
          left: 38px;
          bottom: 16px;
        }
        .pricing-wrap .card--default .notice {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: auto;
          white-space: nowrap;
          font-size: ${font.size.sm[1]};
          line-height: ${font.size.sm[4]};
          color: ${colors.primitive.green[200]};
          background-color: ${colors.primitive.green[100]};
          border-radius: 40px;
        }
        .pricing-wrap .card--default .highlight {
          font-size: ${font.size.lg[2]};
          line-height: ${font.size.lg[3]};
          color: ${colors.primitive.black[700]};
        }
        .pricing-wrap .card--default .plan-user {
          margin-bottom: 5px;
        }
        .pricing-wrap .card--default .ticked-bulletin {
          margin: 20px 0 24px;
        }
        .pricing-wrap .card--default .ticked-bulletin li {
          margin-bottom: 6px;
        }
        .pricing-wrap .card--default .ticked-bulletin li::before {
          margin-right: 8px;
        }
        pricing-wrap .card--default .ticked-bulletin.list-item {
          margin: 0;
        }
        .pricing-wrap .card--default .ticked-bulletin.list-item li {
          margin-bottom: 0;
        }
        .pricing-wrap .card--default .p-block {
          box-shadow: 0 1px 2px 0 rgba(51, 57, 61, 0.2),
            0 1px 4px 0 rgba(51, 57, 61, 0.1);
          padding: 20px;
          width: 100%;
        }
        .pricing-wrap .card--default .p-block > * {
          color: ${colors.primitive.black[100]};
        }
        .pricing-wrap .card--default .p-block li {
          margin-left: 80px;
        }
        .pricing-wrap .card--default .list-item {
          width: 100%;
          margin-bottom: 0;
          padding: 14px 20px 16px;
        }
        .pricing-wrap .card--default .list-item > * {
          font-size: ${font.size.sm[4]};
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .pricing-wrap.card--default > * {
            padding: 20px 40px 104px;
          }
          .pricing-wrap .card--default > :last-child {
            margin-bottom: 0;
          }
          .pricing-wrap .card--default .plan-cta {
            min-height: 40px;
            width: 100%;
            position: unset;
          }
          .pricing-wrap h2 {
            font-size: ${font.size.lg[1]};
            line-height: ${lineHeight[10]};
          }
          .pricing-wrap .card--default.pr-card a.btn-primary,
          .pricing-wrap .card--default.pr-card a.btn-secondary {
            background-color: ${colors.derived.brand};
            color: ${colors.primitive.white.default};
          }
        }
      `}</style>
    </article>
  );
}
